<!-- Updated validation rules: V.1.0.1 -->
<template>
  <div class="register-form">
    <h1 class="form-title">{{ t('auth.register.title') }}</h1>

    <form class="form-container" @submit.prevent="handleRegister">
      <!-- Name Input -->
      <div class="form-group">
        <label for="firstName" class="form-label">{{ t('auth.register.label.name') }}</label>
        <input
          id="firstName"
          name="fname"
          v-model="formData.firstName"
          type="text"
          :class="['form-control', { 'form-control-error': errors.firstName }]"
          :placeholder="t('account.personal_data.label.name')"
          @input="validateFirstName"
        />
        <p :class="['error-message', { 'error-visible': errors.firstName }]">{{ errors.firstName || '&nbsp;' }}</p>
      </div>

      <!-- Surname Input -->
      <div class="form-group">
        <label for="lastName" class="form-label">{{ t('auth.register.label.surname') }}</label>
        <input
          id="lastName"
          name="lname"
          v-model="formData.lastName"
          type="text"
          :class="['form-control', { 'form-control-error': errors.lastName }]"
          :placeholder="t('account.personal_data.label.surname')"
          @input="validateLastName"
        />
        <p :class="['error-message', { 'error-visible': errors.lastName }]">{{ errors.lastName || '&nbsp;' }}</p>
      </div>

      <!-- Phone Input -->
      <div class="form-group">
        <label for="phone" class="form-label">{{ t('auth.register.label.phone') }}</label>
        <input
          id="phone"
          name="phone"
          v-model="formData.phone"
          type="tel"
          :class="['form-control', { 'form-control-error': errors.phone }]"
          :placeholder="t('account.personal_data.label.phone')"
          @input="validatePhone"
        />
        <p :class="['error-message', { 'error-visible': errors.phone }]">{{ errors.phone || '&nbsp;' }}</p>
      </div>

      <!-- Email Input -->
      <div class="form-group">
        <label for="email" class="form-label">{{ t('auth.register.label.email') }}</label>
        <input
          id="email"
          name="email"
          v-model="formData.email"
          type="text"
          :class="['form-control', { 'form-control-error': errors.email }]"
          :placeholder="t('form.placeholder.email')"
          @input="validateEmail"
        />
        <p :class="['error-message', { 'error-visible': errors.email }]">{{ errors.email || '&nbsp;' }}</p>
      </div>

      <!-- Password Input -->
      <div class="form-group">
        <label for="password" class="form-label">{{ t('auth.register.label.pass') }}</label>
        <div class="password-wrapper">
          <input
            id="password"
            name="password"
            v-model="formData.password"
            :type="showPassword ? 'text' : 'password'"
            :class="['form-control', { 'form-control-error': errors.password }]"
            :placeholder="t('account.password_modal.req_min_chars')"
            @blur="validatePassword"
            @input="validatePassword"
            />
          <button type="button" class="password-toggle" @click="showPassword = !showPassword">
            <svg v-if="!showPassword" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
          </button>
        </div>
        <p :class="['error-message', { 'error-visible': errors.password }]">{{ errors.password || '&nbsp;' }}</p>
        <div class="billyb-password-rules">
          <ul class="requirements-list-alt">
            <li :class="{ met: passwordRequirements.hasSpecial }">✓ {{ t('account.password_modal.req_special') }}</li>
            <li :class="{ met: passwordRequirements.minLength }">✓ {{ t('account.password_modal.req_min_chars') }}</li>
            <li :class="{ met: passwordRequirements.hasUppercase }">✓ {{ t('account.password_modal.req_uppercase') }}</li>
            <li :class="{ met: passwordRequirements.hasNumber }">✓ {{ t('account.password_modal.req_number') }}</li>
          </ul>
        </div>
      </div>

      <!-- Confirm Password Input -->
      <div class="form-group">
        <label for="confirmPassword" class="form-label">{{ t('auth.register.label.confirm_pass') }}</label>
        <div class="password-wrapper">
          <input
            id="confirmPassword"
            name="confirm_password"
            v-model="formData.confirmPassword"
            :type="showConfirmPassword ? 'text' : 'password'"
            :class="['form-control', { 'form-control-error': errors.confirmPassword }]"
            :placeholder="t('account.password_modal.confirm_pass')"
            @blur="validateConfirmPassword"
            @input="validateConfirmPassword"
            />
          <button type="button" class="password-toggle" @click="showConfirmPassword = !showConfirmPassword">
            <svg v-if="!showConfirmPassword" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
          </button>
        </div>
        <p :class="['error-message', { 'error-visible': errors.confirmPassword }]">{{ errors.confirmPassword || '&nbsp;' }}</p>
      </div>

      <!-- API Error Message -->
      <div v-if="apiError" class="alert alert-danger">
        {{ t('apiCodes.' + apiError) }}
      </div>

      <!-- GDPR Section -->
      <div class="gdpr-section">
        <div class="custom-checkbox">
          <input
            id="isPrivacyAccepted"
            v-model="formData.isPrivacyAccepted"
            type="checkbox"
            class="form-check-input"
          />
          <label for="isPrivacyAccepted" class="form-check-label">
            <span v-if="currentLanguage === 'it'">
              Accetto i <router-link to="/termini" class="legal-link">Termini di Servizio</router-link> e la <router-link to="/privacy" class="legal-link">Privacy Policy</router-link>
            </span>
            <span v-else>
              I accept the <router-link to="/termini" class="legal-link">Terms of Service</router-link> and <router-link to="/privacy" class="legal-link">Privacy Policy</router-link>
            </span>
          </label>
        </div>

        <div class="custom-checkbox">
          <input
            id="isMarketingAllowed"
            v-model="formData.isMarketingAllowed"
            type="checkbox"
            class="form-check-input"
          />
          <label for="isMarketingAllowed" class="form-check-label">
            {{ t('auth.register.gdpr.marketing_accept') }}
          </label>
        </div>
      </div>

      <!-- Submit Button -->
      <button
        type="submit"
        class="btn-register"
        :disabled="isLoading || cooldown > 0 || !formData.isPrivacyAccepted"
      >
        <span v-if="isLoading" class="spinner"></span>
        {{ isLoading ? t('header.user.registering') : (cooldown > 0 ? `${t('auth.register.btn')} (${cooldown}s)` : t('auth.register.btn')) }}
      </button>

      <!-- Legal notice for reCAPTCHA -->
      <div class="recaptcha-notice" v-html="t('form.recaptcha_notice', { 
        privacy: `<a href='https://policies.google.com/privacy' target='_blank'>${t('form.google_privacy')}</a>`, 
        terms: `<a href='https://policies.google.com/terms' target='_blank'>${t('form.google_terms')}</a>` 
      })"></div>
    </form>

    <!-- Footer Links -->
    <div class="form-footer">
      <router-link to="/login" class="link-login">{{ t('auth.register.login_link') }}</router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import { useConfigStore } from '@/stores/configStore'
import { useLanguage } from '@/composables/useLanguage'
import { useToast } from '@/composables/useToast'

// Auth form logic and validation alignment
const { t, currentLanguage } = useLanguage()
const configStore = useConfigStore()

// Re-validate on language change to update error messages
watch(currentLanguage, () => {
  if (errors.value.firstName) validateFirstName()
  if (errors.value.lastName) validateLastName()
  if (errors.value.phone) validatePhone()
  if (errors.value.email) validateEmail()
  if (errors.value.password) validatePassword()
  if (errors.value.confirmPassword) validateConfirmPassword()
})
const toast = useToast()
const router = useRouter()
const authStore = useAuthStore()
const isLoading = ref(false)
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const cooldown = ref(0)
let cooldownTimer = null

const startCooldown = (seconds) => {
  cooldown.value = seconds
  if (cooldownTimer) clearInterval(cooldownTimer)
  cooldownTimer = setInterval(() => {
    cooldown.value--
    if (cooldown.value <= 0) {
      clearInterval(cooldownTimer)
    }
  }, 1000)
}

onUnmounted(() => {
  if (cooldownTimer) clearInterval(cooldownTimer)
})

const formData = ref({
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  password: '',
  confirmPassword: '',
  isPrivacyAccepted: false,
  isMarketingAllowed: false
})

const errors = ref({
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  password: '',
  confirmPassword: ''
})

const apiError = ref('')

const passwordRequirements = ref({
  show: false,
  minLength: false,
  hasUppercase: false,
  hasNumber: false,
  hasSpecial: false
})

watch(() => formData.value.password, (newPassword) => {
  if (newPassword) {
    passwordRequirements.value.show = true
    passwordRequirements.value.minLength = newPassword.length >= 8
    passwordRequirements.value.hasUppercase = /[A-Z]/.test(newPassword)
    passwordRequirements.value.hasNumber = /[0-9]/.test(newPassword)
    passwordRequirements.value.hasSpecial = /[^a-zA-Z0-9]/.test(newPassword)
  } else {
    passwordRequirements.value.show = false
  }
})

const validateFirstName = () => {
  const nameRegex = /^[\p{L}\p{M}' \.\-]+$/u;
  if (!formData.value.firstName.trim()) {
    errors.value.firstName = t('form.error.required')
  } else if (formData.value.firstName.trim().length < 2) {
    errors.value.firstName = t('form.error.min_length', { count: 2 })
  } else if (!nameRegex.test(formData.value.firstName.trim())) {
    errors.value.firstName = t('form.error.invalid_name')
  } else {
    errors.value.firstName = ''
  }
}

const validateLastName = () => {
  const nameRegex = /^[\p{L}\p{M}' \.\-]+$/u;
  if (!formData.value.lastName.trim()) {
    errors.value.lastName = t('form.error.required')
  } else if (formData.value.lastName.trim().length < 2) {
    errors.value.lastName = t('form.error.min_length', { count: 2 })
  } else if (!nameRegex.test(formData.value.lastName.trim())) {
    errors.value.lastName = t('form.error.invalid_surname')
  } else {
    errors.value.lastName = ''
  }
}

const validatePhone = () => {
  if (formData.value.phone && !/^\+?[0-9\s-]{8,20}$/.test(formData.value.phone)) {
    errors.value.phone = t('form.error.phone_invalid')
  } else {
    errors.value.phone = ''
  }
}

const validateEmail = () => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!formData.value.email) {
    errors.value.email = t('form.error.required')
  } else if (!emailRegex.test(formData.value.email)) {
    errors.value.email = t('form.error.email_invalid')
  } else {
    errors.value.email = ''
  }
}

const validatePassword = () => {
  const password = formData.value.password
  if (!password) {
    errors.value.password = t('form.error.required')
  } else if (password.length < 8) {
    errors.value.password = t('form.error.password_min')
  } else if (!/[A-Z]/.test(password)) {
    errors.value.password = t('form.error.password_uppercase')
  } else if (!/[0-9]/.test(password)) {
    errors.value.password = t('form.error.password_number')
  } else if (!/[^a-zA-Z0-9]/.test(password)) {
    errors.value.password = t('form.error.password_special')
  } else {
    errors.value.password = ''
  }
}

const validateConfirmPassword = () => {
  if (formData.value.confirmPassword !== formData.value.password) {
    errors.value.confirmPassword = t('form.error.mismatch')
  } else {
    errors.value.confirmPassword = ''
  }
}

const generateRecaptchaToken = async () => {
  if (!configStore.recaptchaSiteKey) return ''
  
  const waitForRecaptcha = () => {
    return new Promise((resolve) => {
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        resolve(true)
        return
      }
      
      let attempts = 0
      const interval = setInterval(() => {
        attempts++
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          clearInterval(interval)
          resolve(true)
        } else if (attempts > 50) {
          clearInterval(interval)
          resolve(false)
        }
      }, 100)
    })
  }

  const isRecaptchaReady = await waitForRecaptcha()
  if (!isRecaptchaReady) return ''

  return await new Promise((resolve) => {
    window.grecaptcha.enterprise.ready(async () => {
      try {
        const token = await window.grecaptcha.enterprise.execute(configStore.recaptchaSiteKey, { action: 'register' })
        resolve(token)
      } catch (e) {
        console.error('[RECAPTCHA] Execute error:', e)
        resolve('')
      }
    })
  })
}

const handleRegister = async () => {
  validateFirstName()
  validateLastName()
  validatePhone()
  validateEmail()
  validatePassword()
  validateConfirmPassword()

  if (errors.value.firstName || errors.value.lastName || errors.value.phone || errors.value.email || errors.value.password || errors.value.confirmPassword) {
    return
  }

  apiError.value = ''
  isLoading.value = true

  try {
    const recaptchaToken = await generateRecaptchaToken()
    if (!recaptchaToken) {
      apiError.value = 'AUTH_RECAPTCHA_REQUIRED'
      isLoading.value = false
      return
    }
    const result = await authStore.register(
      formData.value.email,
      formData.value.password,
      formData.value.firstName,
      formData.value.lastName,
      formData.value.phone,
      formData.value.isPrivacyAccepted,
      formData.value.isMarketingAllowed,
      recaptchaToken
    )
    if (result && result.success) {
      router.push('/register-success')
    } else {
      const code = result?.code || 'SYSTEM_ERROR'
      apiError.value = code
      if (code === 'AUTH_RATE_LIMITED') {
        startCooldown(30)
      }
    }
  } catch (err) {
    const code = err?.response?.data?.code || 'SYSTEM_ERROR'
    apiError.value = code
    if (code === 'AUTH_RATE_LIMITED') {
      startCooldown(30)
    }
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.register-form {
  position: relative;
  z-index: 10;
}

.form-title {
  text-align: center;
  font-family: 'Montserrat', sans-serif;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: #000000;
  margin-bottom: 28px;
  text-transform: uppercase;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #444444;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.form-control {
  width: 100%;
  padding: 13px 14px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #000000;
  border: 1.5px solid #e5e5e5;
  border-radius: 6px;
  background-color: #ffffff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: var(--dark-teal);
  box-shadow: 0 0 0 4px rgba(0,140,149,0.12);
}

.form-control::placeholder {
  color: #bbbbbb;
}

.form-control:hover {
  border-color: #cccccc;
}

.form-control-error {
  border-color: #d9534f !important;
}

.password-wrapper {
  position: relative;
  width: 100%;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent !important;
  border: none !important;
  outline: none !important;
  cursor: pointer;
  color: #888888;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  height: 100%;
}

.password-toggle:hover {
  color: var(--dark-teal);
}

.password-toggle svg {
  display: block;
}

.error-message {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  color: #d9534f;
  margin-top: 6px;
  font-weight: 600;
  height: 14px;
  opacity: 0;
  transition: opacity 0.3s ease;
  letter-spacing: 0.3px;
}

.error-message.error-visible {
  opacity: 1;
}

.billyb-password-rules {
  margin-top: 18px;
  padding: 18px 20px;
  background: #fafafa;
  border: 1px solid #eaeaea;
  border-radius: 6px;
}

.requirements-list-alt {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

.requirements-list-alt li {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #888888;
  text-transform: none;
  letter-spacing: 0.2px;
  font-weight: 500;
  line-height: 1.45;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.requirements-list-alt li::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #dddddd;
  flex-shrink: 0;
  transition: all 0.3s ease;
  margin-top: 5px;
}

.requirements-list-alt li.met {
  color: #2d5559;
  font-weight: 600;
}

.requirements-list-alt li.met::before {
  background: var(--bright-teal);
  box-shadow: 0 0 6px rgba(44, 188, 196, 0.4);
}

.alert {
  padding: 14px;
  margin-bottom: 24px;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid #d9534f;
  color: #d9534f;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  text-align: center;
  background: rgba(217, 83, 79, 0.05);
  border-radius: 6px;
}

.alert-success {
  border-color: var(--bright-teal);
  color: var(--bright-teal);
  background: rgba(44, 188, 196, 0.05);
}

.btn-register {
  width: 100%;
  padding: 15px;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  background: #000000;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: background 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 6px;
}

.btn-register:hover:not(:disabled) {
  background: var(--dark-teal);
  box-shadow: 0 8px 20px -6px rgba(0,140,149,0.5);
}

.btn-register:active:not(:disabled) {
  transform: translateY(1px);
}

.btn-register:disabled {
  background: #5C8889;
  opacity: 0.5;
  cursor: not-allowed;
}

.gdpr-section {
  margin: 20px 0;
  text-align: left;
}

.custom-checkbox {
  margin-bottom: 15px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.custom-checkbox .form-check-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.custom-checkbox .form-check-label {
  position: relative;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #666666;
  line-height: 1.4;
  cursor: pointer;
  user-select: none;
  padding-left: 26px;
  padding-top: 1px;
}

.custom-checkbox .form-check-label::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 18px;
  height: 18px;
  border: 1.5px solid #e5e5e5;
  border-radius: 3px;
  background: #ffffff;
  transition: all 0.2s ease;
}

.custom-checkbox .form-check-label::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 3px;
  width: 5px;
  height: 9px;
  border: solid #ffffff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg) scale(0);
  transition: transform 0.15s ease;
}

.custom-checkbox .form-check-input:checked + .form-check-label::before {
  background: var(--dark-teal);
  border-color: var(--dark-teal);
}

.custom-checkbox .form-check-input:checked + .form-check-label::after {
  transform: rotate(45deg) scale(1);
}

.custom-checkbox .form-check-input:focus + .form-check-label::before {
  box-shadow: 0 0 0 4px rgba(0,140,149,0.12);
}

.legal-link {
  color: var(--dark-teal);
  text-decoration: none;
  font-weight: 700;
  position: relative;
  padding-bottom: 1px;
  transition: color 0.2s ease;
}

.legal-link::after {
  content: '';
  position: absolute;
  left: 0; right: 0; bottom: -1px;
  height: 1.5px;
  background: var(--bright-teal);
  transform: scaleX(0);
  transform-origin: center;
  transition: transform 0.25s ease;
}

.legal-link:hover {
  color: var(--bright-teal);
}

.legal-link:hover::after {
  transform: scaleX(1);
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.recaptcha-notice {
  text-align: center;
  font-size: 11px;
  line-height: 18px;
  color: #888888;
  margin: 16px 0 22px;
}

.recaptcha-notice a {
  color: #888888;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.recaptcha-notice a:hover {
  color: var(--dark-teal);
}

.form-footer {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}

.form-footer p {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #888888;
  margin: 0;
}

.link-login {
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--dark-teal);
  text-decoration: none;
  position: relative;
  padding-bottom: 2px;
  transition: color 0.2s ease;
}

.link-login::after {
  content: '';
  position: absolute;
  left: 0; right: 0; bottom: -1px;
  height: 1.5px;
  background: var(--bright-teal);
  transform: scaleX(0);
  transform-origin: center;
  transition: transform 0.25s ease;
}

.link-login:hover {
  color: var(--bright-teal);
}

.link-login:hover::after {
  transform: scaleX(1);
}

@media (max-width: 600px) {
  .form-title {
    font-size: 18px;
    margin-bottom: 24px;
  }

  .btn-register {
    padding: 14px;
    font-size: 13px;
  }
}
</style>
